import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Check, Loader2, Search, UserRound, X } from 'lucide-react';
import { defaultInstance } from '../../../api/httpClient';
import {
  CLIENT_SEARCH_LIMIT, CLIENT_SEARCH_MIN_CHARS, clientDetail, clientName, clientRowsOf, clientSearchQueries,
  mergeClientResults, type ClientRow,
} from './clientSearch';

/**
 * SAM-198 — «Existing client» in Client booking: search the tenant's app users (GET /crud/user,
 * see clientSearch.ts for how one box becomes name / phone / email / cédula filters) and pick
 * one. The service booking then sends `on_behalf_of: {user_id}`. Read-only: never creates or
 * edits a user. New Tailwind component (SAM-175 freeze).
 */

export function useClientSearch(input: string) {
  const [debounced, setDebounced] = useState(input);
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(input), 300);
    return () => window.clearTimeout(t);
  }, [input]);
  const queries = clientSearchQueries(debounced);
  const query = useQuery({
    queryKey: ['client-search', queries],
    queryFn: async () => {
      const lists = await Promise.all(queries.map((params) =>
        defaultInstance({ url: '/user', method: 'GET', params: { ...params, limit: CLIENT_SEARCH_LIMIT } })
          .then(clientRowsOf)));
      return mergeClientResults(lists);
    },
    enabled: queries.length > 0,
    staleTime: 30000,
    retry: false,
  });
  return { ...query, searching: queries.length > 0, pending: input !== debounced };
}

export const ExistingClientPicker: React.FC<{
  selected: ClientRow | null;
  onSelect: (row: ClientRow | null) => void;
}> = ({ selected, onSelect }) => {
  const [input, setInput] = useState('');
  const { data: results = [], isFetching, isError, refetch, searching, pending } = useClientSearch(input);

  if (selected) {
    return (
      <div
        data-testid="existing-client-selected"
        className="flex items-center gap-3 rounded-[10px] border border-solid border-[var(--tenant-primary,#005488)] bg-white p-3"
      >
        <span className="w-10 h-10 shrink-0 rounded-full bg-[var(--line-subtle)] text-[var(--ink-700)] flex items-center justify-center">
          <UserRound size={18} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-1.5 text-[15px] font-semibold text-[var(--ink-900)]">
            <Check size={14} className="text-[var(--ok-fg)] shrink-0" /> <span className="truncate">{clientName(selected)}</span>
          </span>
          <span className="block text-[13px] text-[var(--ink-500)] break-words">{clientDetail(selected) || 'No contact details on file'}</span>
        </span>
        <button
          type="button"
          onClick={() => onSelect(null)}
          className="h-9 px-3 shrink-0 rounded-[8px] border border-solid border-[var(--line)] bg-white text-[13px] font-semibold text-[var(--ink-700)] cursor-pointer"
        >
          Change
        </button>
      </div>
    );
  }

  const short = input.trim().length < CLIENT_SEARCH_MIN_CHARS;
  return (
    <div className="flex flex-col gap-2">
      <label className="flex items-center gap-2 h-11 px-3 rounded-[8px] border border-solid border-[var(--line)] bg-white text-[var(--ink-400)]">
        <Search size={16} className="shrink-0" />
        <input
          aria-label="Search app users"
          placeholder="Name, phone, email or ID number"
          value={input}
          autoComplete="off"
          onChange={(e) => setInput(e.target.value)}
          className="flex-1 min-w-0 bg-transparent border-0 outline-none text-[15px] text-[var(--ink-900)]"
        />
        {(isFetching || pending) && searching && <Loader2 size={15} className="animate-spin shrink-0" />}
        {input && (
          <button type="button" aria-label="Clear search" onClick={() => setInput('')} className="w-7 h-7 shrink-0 rounded-md border-0 bg-transparent cursor-pointer text-[var(--ink-400)] flex items-center justify-center">
            <X size={14} />
          </button>
        )}
      </label>

      {short ? (
        <p className="m-0 text-[13px] text-[var(--ink-500)]">Type at least {CLIENT_SEARCH_MIN_CHARS} characters to search the app&apos;s users.</p>
      ) : isError ? (
        <p role="alert" className="m-0 text-[13px] text-[var(--bad-fg)]">
          Search failed.{' '}
          <button type="button" onClick={() => refetch()} className="border-0 bg-transparent p-0 font-semibold underline cursor-pointer text-[var(--bad-fg)]">Try again</button>
        </p>
      ) : !isFetching && !pending && results.length === 0 ? (
        <p className="m-0 text-[13px] text-[var(--ink-500)]">No app user matches. Use «New client» to enter their details instead.</p>
      ) : (
        <ul aria-label="Matching app users" className="m-0 p-0 list-none flex flex-col rounded-[10px] border border-solid border-[var(--line)] bg-white overflow-hidden">
          {results.map((r, i) => (
            <li key={String(r.id)} className={i ? 'border-0 border-t border-solid border-[var(--line-subtle)]' : ''}>
              <button
                type="button"
                onClick={() => onSelect(r)}
                className="w-full text-left flex items-center gap-3 px-3 py-2.5 border-0 bg-white cursor-pointer hover:bg-[var(--surface-2,#f9f9f9)]"
              >
                <UserRound size={16} className="shrink-0 text-[var(--ink-400)]" />
                <span className="min-w-0">
                  <span className="block text-[14px] font-semibold text-[var(--ink-900)] truncate">{clientName(r)}</span>
                  {clientDetail(r) && <span className="block text-[12px] text-[var(--ink-500)] break-words">{clientDetail(r)}</span>}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
